import { ActionBar, RowsSelected } from "@/components/shared/tables/ActionBar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  InvoiceType,
  type InvoiceTypeString,
} from "@/lib/constants/invoice-type";
import { InvoiceStatus } from "@/lib/constants/invoiceStatus";

import { invoiceExtractionRetry } from "@/apis/custom/customRoutes";
import {
  ExportedIcon,
  ReviewedIcon,
  ToReviewIcon,
} from "@/components/shared/status/invoice";
import { usePermissions } from "@/hooks/usePermissions";
import type { Exports } from "@/lib/constants/exports";
import { useExportHistory } from "@/providers/invoices";
import { useDeleteInvoice } from "@/queries/invoices/delete";
import {
  useUpdateInvoicesStatus,
  useUpdateInvoicesType,
} from "@/queries/invoices/update";
import { Check } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import ExportButtonNew from "./export/ExportButton";
import { useInvoiceTableInstanceContext } from "./table-instance-provider";

type Props = {
  invoiceType?: InvoiceTypeString;
  actionsConfig: {
    exportButton?: {
      isVisible: boolean;
      options?: Exports[];
    };
    showDeleteButton?: boolean;
    showStatusUpdateButton?: boolean;
    showTypeUpdateButton?: boolean;
    showRetryAiButton?: boolean;
  };
};

export const InvoiceTableActionBar = ({
  invoiceType,
  actionsConfig: {
    exportButton = {
      isVisible: true,
      options: undefined,
    },
    showDeleteButton = true,
    showStatusUpdateButton = true,
    showTypeUpdateButton = true,
    showRetryAiButton = true,
  },
}: Props) => {
  const { table, setRowSelection } = useInvoiceTableInstanceContext();

  const updateInvoiceStatus = useUpdateInvoicesStatus();
  const [updateInvoiceType] = useUpdateInvoicesType();
  const deleteInvoices = useDeleteInvoice();
  const { refetchExportHistory } = useExportHistory();

  const { isSuperAdmin } = usePermissions();
  const [isRetrying, setIsRetrying] = useState(false);

  const handleExportSuccess = () => {
    refetchExportHistory();
  };

  const handleStatusUpdate = async (status: InvoiceStatus) => {
    try {
      await updateInvoiceStatus(selectedInvoiceIds, status);
    } catch (error) {}
  };

  const handleTypeUpdate = async (type: InvoiceType) => {
    if (invoiceType === type) return;
    try {
      await updateInvoiceType(selectedInvoiceIds, type);
    } catch (error) {}
  };

  const handleDelete = async () => {
    setRowSelection([]);
    try {
      await deleteInvoices(selectedInvoiceIds);
    } catch (error) {}
  };

  const selectedRows = table ? table.getFilteredSelectedRowModel().rows : [];
  const selectedInvoiceIds =
    selectedRows.length > 0
      ? selectedRows.map((row) => row.original?.invoice.invoiceId)
      : [];

  const selectedInvoiceRows = selectedRows.length > 0 ? selectedRows : [];

  const allRows = table ? table.getFilteredRowModel().rows : [];
  const allInvoiceIds =
    allRows.length > 0
      ? allRows.map((row) => row.original?.invoice.invoiceId)
      : [];

  const handleRetryAi = async () => {
    setIsRetrying(true);
    toast.info("AI retry started...");
    try {
      await Promise.all(
        selectedInvoiceIds.map((id) => invoiceExtractionRetry(id)),
      );
      toast.success("AI retry triggered successfully");
    } catch (error) {
      toast.error("Failed to trigger AI retry");
    } finally {
      setIsRetrying(false);
    }
  };

  if (selectedRows.length <= 0) return null;

  return (
    <ActionBar>
      <RowsSelected
        selectedRowsLength={selectedRows.length}
        totalRowsLength={allRows.length}
        onDeselectAll={() => table?.resetRowSelection(true)}
      />
      {showStatusUpdateButton && <StatusSelect onSelect={handleStatusUpdate} />}
      {showTypeUpdateButton && (
        <TypeSelect currentType={invoiceType} onSelect={handleTypeUpdate} />
      )}
      {showDeleteButton && (
        <Button variant={"outline"} onClick={() => handleDelete()}>
          Löschen
        </Button>
      )}
      {isSuperAdmin && showRetryAiButton && (
        <Button
          className="bg-pink-500 hover:bg-pink-600 text-white"
          onClick={handleRetryAi}
          disabled={isRetrying}
        >
          {isRetrying ? "Retrying..." : "Retry AI"}
        </Button>
      )}
      {exportButton.isVisible && (
        <ExportButtonNew
          selectedInvoiceRows={selectedInvoiceRows}
          allInvoiceIds={allInvoiceIds}
          options={exportButton.options}
          onSuccess={handleExportSuccess}
          buttonStyle={{ variant: "outline" }}
        />
      )}
    </ActionBar>
  );
};

export function StatusSelect({
  onSelect,
}: {
  onSelect: (value: InvoiceStatus) => void;
}) {
  const { t } = useTranslation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={"outline"}>Status Ändern</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        <DropdownMenuLabel>Status</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => onSelect(InvoiceStatus.PREPROCESSED)}>
          <ToReviewIcon />
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onSelect(InvoiceStatus.REVIEWED)}>
          <ReviewedIcon />
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onSelect(InvoiceStatus.EXPORTED)}>
          <ExportedIcon />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TypeSelect({
  currentType,
  onSelect,
}: {
  currentType?: InvoiceTypeString;
  onSelect: (value: InvoiceType) => void;
}) {
  const { t } = useTranslation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={"outline"}>Typ Ändern</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        <DropdownMenuLabel>Typ</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => onSelect(InvoiceType.PAYABLE)}>
          <span className="flex w-full items-center justify-between">
            {t("invoices-table.invoice_type.payable")}
            {currentType === InvoiceType.PAYABLE && (
              <Check className="h-4 w-4" />
            )}
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onSelect(InvoiceType.RECEIVABLE)}>
          <span className="flex w-full items-center justify-between">
            {t("invoices-table.invoice_type.receivable")}
            {currentType === InvoiceType.RECEIVABLE && (
              <Check className="h-4 w-4" />
            )}
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
